import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import type { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { colorToHex } from "@/lib/colorMap";
import { Price } from "./Price";

interface Props {
  product: ShopifyProduct;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickViewDialog({ product, open, onOpenChange }: Props) {
  const { node } = product;
  const addItem = useCartStore(s => s.addItem);
  const isLoading = useCartStore(s => s.isLoading);

  const variants = node.variants.edges;
  const [variantIdx, setVariantIdx] = useState(0);
  const variant = variants[variantIdx]?.node;
  const image = node.images.edges[0]?.node;
  const price = variant?.price ?? node.priceRange.minVariantPrice;

  const optionTypes = useMemo(() => node.options ?? [], [node.options]);

  const handleAddToCart = async () => {
    if (!variant) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    toast.success(`${node.title} added to cart`, { position: "bottom-right" });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#FDFAF3] p-0 border-[#D4CCBC]">
        <DialogTitle className="sr-only">{node.title}</DialogTitle>
        <DialogDescription className="sr-only">Quick view for {node.title}</DialogDescription>
        <div className="grid gap-0 md:grid-cols-2">
          {/* Image */}
          <div className="aspect-square overflow-hidden bg-[#F5EFE2]">
            {image ? (
              <img src={image.url} alt={image.altText || node.title} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center font-ui text-sm text-[#8B6F47]">No image</div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col gap-4 p-6 md:p-8">
            <div>
              <p className="font-ui text-[10px] font-bold uppercase tracking-[0.25em] text-saddle">Quick View</p>
              <h2 className="mt-1 font-heading text-2xl font-bold uppercase text-[#2A1510]">{node.title}</h2>
              <p className="mt-1 font-body text-2xl font-bold text-[#8B6F47]"><Price amount={price.amount} /></p>
            </div>

            {node.description && (
              <p className="line-clamp-3 font-body text-sm text-[#2A1510]/80">{node.description}</p>
            )}

            {/* Variant pickers */}
            {optionTypes.map((opt) => {
              const isColor = opt.name.toLowerCase() === "color";
              return (
                <div key={opt.name}>
                  <label className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#2A1510]">
                    {opt.name}: <span className="text-[#8B6F47]">{variant?.selectedOptions.find(o => o.name === opt.name)?.value}</span>
                  </label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {opt.values.map((value) => {
                      const idx = variants.findIndex(v => v.node.selectedOptions.some(o => o.name === opt.name && o.value === value));
                      const selected = idx === variantIdx;
                      const available = idx >= 0 && variants[idx].node.availableForSale;
                      if (isColor) {
                        return (
                          <button
                            key={value}
                            onClick={() => idx >= 0 && setVariantIdx(idx)}
                            title={value}
                            className={`h-7 w-7 rounded-full border-2 transition-all ${selected ? "border-[#FA5021] ring-2 ring-[#FA5021]/30 ring-offset-2 ring-offset-[#FDFAF3]" : "border-[#D4CCBC]"} ${!available ? "opacity-40" : ""}`}
                            style={{ backgroundColor: colorToHex(value) }}
                          />
                        );
                      }
                      return (
                        <button
                          key={value}
                          onClick={() => idx >= 0 && setVariantIdx(idx)}
                          className={`min-w-[44px] rounded-sm border px-3 py-1.5 font-ui text-xs font-semibold uppercase tracking-wider transition-all ${selected ? "border-[#FA5021] bg-[#FA5021]/10 text-[#2A1510]" : "border-[#D4CCBC] text-[#2A1510] hover:border-[#2A1510]"} ${!available ? "opacity-40 line-through" : ""}`}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <button
              onClick={handleAddToCart}
              disabled={isLoading || !variant?.availableForSale}
              className="w-full btn-cc-accent tracking-[0.2em]"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : variant?.availableForSale ? "Add to Cart" : "Sold Out"}
            </button>

            <Link
              to="/products/$handle"
              params={{ handle: node.handle }}
              onClick={() => onOpenChange(false)}
              className="text-center font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#8B6F47] underline-offset-4 hover:text-[#FA5021] hover:underline"
            >
              View Full Details →
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
